($ as any).validator.addMethod("filesize", function (value: string, element: HTMLInputElement, param: any) {
  // If no file selected, validation passes (use [Required] for mandatory files)
  if (!element || !element.files || element.files.length === 0) {
    return true;
  }

  const file = element.files[0];
  const fileSizeMB = file.size / (1024 * 1024); // Convert bytes to MB

  const minSize = parseInt($(element).data('val-filesize-min'));
  const maxSize = parseInt($(element).data('val-filesize-max'));

  console.debug('File validation:', {
    filename: file.name,
    sizeMB: Math.round(fileSizeMB * 100) / 100,
    minSize,
    maxSize
  });

  // Check minimum file size
  if (!isNaN(minSize) && fileSizeMB < minSize) {
    return false;
  }

  // Check maximum file size
  if (!isNaN(maxSize) && fileSizeMB > maxSize) {
    return false;
  }

  return true;
});

($ as any).validator.unobtrusive.adapters.add("filesize", function (options: any) {
  options.rules["filesize"] = true;
  if (options.message) {
    options.messages["filesize"] = options.message;
  }
});

// ============================================================
// Image Size Validation (Constraint Validation API + HTMX)
// ============================================================

interface ImageDimensionCache {
  key: string;
  width: number;
  height: number;
}

const dimensionCache = new WeakMap<HTMLInputElement, ImageDimensionCache>();

function getFileKey(file: File): string {
  return `${file.name}_${file.size}_${file.lastModified}`;
}

function buildImageSizeErrorMessage(element: HTMLInputElement, width: number, height: number): string | null {
  const minWidth = parseInt(element.getAttribute('data-val-imagesize-minwidth') ?? '');
  const maxWidth = parseInt(element.getAttribute('data-val-imagesize-maxwidth') ?? '');
  const minHeight = parseInt(element.getAttribute('data-val-imagesize-minheight') ?? '');
  const maxHeight = parseInt(element.getAttribute('data-val-imagesize-maxheight') ?? '');

  if (!isNaN(minWidth) && !isNaN(maxWidth) && minWidth === maxWidth && width !== minWidth) {
    return `Image width must be exactly ${minWidth}px (was ${width}px).`;
  }
  if (!isNaN(minWidth) && width < minWidth) {
    return `Image width must be at least ${minWidth}px (was ${width}px).`;
  }
  if (!isNaN(maxWidth) && width > maxWidth) {
    return `Image width must be at most ${maxWidth}px (was ${width}px).`;
  }

  if (!isNaN(minHeight) && !isNaN(maxHeight) && minHeight === maxHeight && height !== minHeight) {
    return `Image height must be exactly ${minHeight}px (was ${height}px).`;
  }
  if (!isNaN(minHeight) && height < minHeight) {
    return `Image height must be at least ${minHeight}px (was ${height}px).`;
  }
  if (!isNaN(maxHeight) && height > maxHeight) {
    return `Image height must be at most ${maxHeight}px (was ${height}px).`;
  }

  return null;
}

function validateImageSize(input: HTMLInputElement): void {
  input.setCustomValidity('');

  if (!input.files || input.files.length === 0) {
    return;
  }

  const file = input.files[0];

  if (file.name.toLowerCase().endsWith('.svg')) {
    return;
  }

  const cached = dimensionCache.get(input);
  if (cached && cached.key === getFileKey(file)) {
    const error = buildImageSizeErrorMessage(input, cached.width, cached.height);
    if (error) {
      input.setCustomValidity(error);
    }
    return;
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  const expectedKey = getFileKey(file);

  img.onload = () => {
    URL.revokeObjectURL(url);

    const currentFile = input.files?.[0];
    if (!currentFile || getFileKey(currentFile) !== expectedKey) {
      return;
    }

    dimensionCache.set(input, {
      key: expectedKey,
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
    const error = buildImageSizeErrorMessage(input, img.naturalWidth, img.naturalHeight);
    if (error) {
      input.setCustomValidity(error);
      input.reportValidity();
    }
  };

  img.onerror = () => {
    URL.revokeObjectURL(url);
  };

  img.src = url;
}

function bindImageSizeValidation(root: Element | Document): void {
  root.querySelectorAll<HTMLInputElement>('input[data-val-imagesize]')
    .forEach(input => {
      if (input.dataset.imageSizeBound) return;
      input.dataset.imageSizeBound = 'true';
      input.addEventListener('change', () => validateImageSize(input));
    });
}

bindImageSizeValidation(document);

document.addEventListener('htmx:load', (event: Event) => {
  const target = (event as CustomEvent).detail?.elt;
  if (target instanceof Element) {
    bindImageSizeValidation(target);
  }
});
